/**
 * Chat Input Component
 *
 * Text input with send button for the AI Chat.
 */

import React, { useRef, useCallback, KeyboardEvent } from 'react';
import { Box, IconButton, TextField, Tooltip, CircularProgress } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import StopIcon from '@mui/icons-material/Stop';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import useAIChatStore from '../../../stores/ai-chat-store';
import { dc } from '../design-tokens';

interface ChatInputProps {
  onSend: (message: string) => void;
  onStop?: () => void;
  disabled?: boolean;
  isSending?: boolean;
  isStreaming?: boolean;
  placeholder?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  onStop,
  disabled = false,
  isSending = false,
  isStreaming = false,
  placeholder,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const { inputValue, setInputValue, clearInput } = useAIChatStore((state) => ({
    inputValue: state.inputValue,
    setInputValue: state.setInputValue,
    clearInput: state.clearInput,
  }));

  const handleSubmit = useCallback(() => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && !disabled && !isSending) {
      onSend(trimmedValue);
      clearInput();
      // Keep focus on input after sending
      inputRef.current?.focus();
    }
  }, [inputValue, disabled, isSending, onSend, clearInput]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
    },
    [setInputValue]
  );

  const canSend = inputValue.trim().length > 0 && !disabled && !isSending;

  return (
    <Box className={classes.inputContainer}>
      <Box className={classes.inputWrapper}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || t('askAnything', 'Ask me anything...')}
          disabled={disabled || isSending}
          variant="outlined"
          size="small"
          className={classes.textField}
          InputProps={{
            classes: {
              root: classes.inputRoot,
              input: classes.input,
            },
          }}
        />

        <Box className={classes.actions}>
          {isStreaming ? (
            <Tooltip title={t('stop', 'Stop')}>
              <span>
                <IconButton
                  onClick={onStop}
                  disabled={!onStop}
                  className={classes.stopButton}
                  size="small"
                >
                  <StopIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={t('send', 'Send')}>
              <span>
                <IconButton
                  onClick={handleSubmit}
                  disabled={!canSend}
                  className={classes.sendButton}
                  size="small"
                >
                  {isSending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <ArrowUpwardIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box className={classes.disclaimer}>
        <span>{t('aiDisclaimer', 'AI may produce inaccurate information. Verify important details.')}</span>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  inputContainer: {
    padding: theme.spacing(0, 3, 1.5, 3),
    backgroundColor: 'transparent',
    maxWidth: 660,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    backgroundColor: dc.surface,
    borderRadius: 18,
    padding: theme.spacing(0.9, 0.9, 0.9, 1.9),
    border: `1px solid ${dc.border}`,
    boxShadow: dc.shadow,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    '&:focus-within': {
      borderColor: dc.primary,
      boxShadow: `0 0 0 2px ${dc.primary}22, ${dc.shadow}`,
    },
  },
  textField: {
    flex: 1,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiInputBase-root': {
      padding: 0,
      color: theme.palette.text.primary,
    },
  },
  inputRoot: {
    backgroundColor: 'transparent',
    fontSize: 15,
    lineHeight: 1.6,
    color: dc.ink,
    '& fieldset': {
      border: 'none',
    },
  },
  input: {
    padding: theme.spacing(1, 0),
    color: dc.ink,
    '&::placeholder': {
      color: dc.faint,
      opacity: 1,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  sendButton: {
    backgroundColor: dc.primary,
    color: '#fff',
    width: 34,
    height: 34,
    borderRadius: 10,
    '&:hover': {
      backgroundColor: '#2B0FB3',
    },
    '&.Mui-disabled': {
      backgroundColor: dc.sendDisabled,
      color: '#fff',
    },
  },
  stopButton: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    width: 36,
    height: 36,
    '&:hover': {
      backgroundColor: theme.palette.error.main,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.greys.lighter,
      color: theme.palette.greys.medium,
    },
  },
  disclaimer: {
    textAlign: 'center',
    marginTop: theme.spacing(0.9),
    '& span': {
      fontSize: '0.65rem',
      color: dc.faint,
    },
  },
}));

export default ChatInput;

